import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteCorpusDocumentsApiAdminVectorstoreDeletionsPost,
  getCorpusAccessApiAdminVectorstoreAccessGet,
  getCorpusDocumentApiAdminVectorstoreDocumentsDetailGet,
  getCorpusFieldValuesApiAdminVectorstoreValuesGet,
  getCorpusOverviewApiAdminVectorstoreOverviewGet,
  previewCorpusDeletionApiAdminVectorstoreDeletionsPreviewPost,
  queryCorpusDocumentsApiAdminVectorstoreDocumentsQueryPost,
  type CorpusDocumentQuery,
  type DeletionPreview,
  type DeletionPreviewRequest,
  type DeletionResult,
} from "@noa/api-core";
import { ApiError } from "../../errors";
import { CorpusQueryKey } from "../../query-keys";

/** A client error (a 4xx such as a query too broad) answers the same on every try. */
const retryServerErrors = (failureCount: number, error: Error): boolean =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failureCount < 3;

/** The corpus changes with a sync or a delete, which the page refreshes after; not on every focus. */
const STALE_AFTER_MS = 30_000;

/**
 * Keep the shown page while the next page of the same document loads, and never show a
 * document's chunks under another one (`detail` keys hold the URL at index 2).
 */
export const keepPageOfSameDocument =
  (sourceUrl: string | null) =>
  <T>(previous: T | undefined, previousQuery?: { queryKey: readonly unknown[] }): T | undefined =>
    previousQuery?.queryKey[2] === sourceUrl ? previous : undefined;

/** What the signed-in user may do on the page; nothing beyond reading until the server says so. */
export function useCorpusAccess() {
  const { data, isPending } = useQuery({
    queryKey: CorpusQueryKey.access(),
    queryFn: async () => {
      const { data } = await getCorpusAccessApiAdminVectorstoreAccessGet({ throwOnError: true });
      return data;
    },
    // The role changes only in the Clerk dashboard: a reload of the page picks the change up
    staleTime: Infinity,
    retry: retryServerErrors,
  });

  return { canDelete: data?.can_delete ?? false, isLoading: isPending };
}

export function useCorpusOverview() {
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: CorpusQueryKey.overview(),
    queryFn: async () => {
      const { data } = await getCorpusOverviewApiAdminVectorstoreOverviewGet({ throwOnError: true });
      return data;
    },
    staleTime: STALE_AFTER_MS,
    retry: retryServerErrors,
  });

  return { overview: data, isLoading: isPending, isError, error, refetch };
}

/** The values a schema field takes on documents of a type; idle until a type is chosen. */
export function useCorpusFieldValues(documentType: string | undefined, field: string) {
  const { data, isPending } = useQuery({
    queryKey: CorpusQueryKey.values(documentType ?? "", field),
    queryFn: async () => {
      const { data } = await getCorpusFieldValuesApiAdminVectorstoreValuesGet({
        query: { document_type: documentType!, field },
        throwOnError: true,
      });
      return data;
    },
    enabled: !!documentType,
    staleTime: STALE_AFTER_MS,
    retry: retryServerErrors,
  });

  return { values: data ?? [], isLoading: !!documentType && isPending };
}

/** A page of the stored documents; the previous page stays shown while the next one loads. */
export function useCorpusDocuments(query: CorpusDocumentQuery) {
  const { data, isPending, isFetching, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: CorpusQueryKey.documents(query),
    queryFn: async () => {
      const { data } = await queryCorpusDocumentsApiAdminVectorstoreDocumentsQueryPost({
        body: query,
        throwOnError: true,
      });
      return data;
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_AFTER_MS,
    retry: retryServerErrors,
  });

  return {
    documents: data?.items ?? [],
    total: data?.total ?? 0,
    isLoading: isPending,
    isFetching,
    /** Whether the documents shown are the previous query's, until this one answers. */
    isPlaceholderData,
    isError,
    error,
    refetch,
  };
}

/** A document's records and a page of its chunks; idle while no document is open. */
export function useCorpusDocumentDetail(sourceUrl: string | null, chunkOffset: number, chunkLimit: number) {
  const { data, isPending, isFetching, isError, error } = useQuery({
    queryKey: CorpusQueryKey.detail(sourceUrl ?? "", chunkOffset, chunkLimit),
    queryFn: async () => {
      const { data } = await getCorpusDocumentApiAdminVectorstoreDocumentsDetailGet({
        query: { source_url: sourceUrl!, chunk_offset: chunkOffset, chunk_limit: chunkLimit },
        throwOnError: true,
      });
      return data;
    },
    enabled: !!sourceUrl,
    placeholderData: keepPageOfSameDocument(sourceUrl),
    staleTime: STALE_AFTER_MS,
    retry: retryServerErrors,
  });

  return { detail: data, isLoading: !!sourceUrl && isPending, isFetching, isError, error };
}

/** What deleting the selection would remove; idle while nothing is selected. Read-only on the server. */
export function useCorpusDeletionPreview(request: DeletionPreviewRequest | null) {
  const { data, isPending, isError, error } = useQuery<DeletionPreview, ApiError>({
    queryKey: CorpusQueryKey.preview(request),
    queryFn: async () => {
      const { data } = await previewCorpusDeletionApiAdminVectorstoreDeletionsPreviewPost({
        body: request!,
        throwOnError: true,
      });
      return data;
    },
    enabled: request !== null,
    // A preview must show the store as it is now, never a cached answer
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: retryServerErrors,
  });

  return { preview: data, isLoading: request !== null && isPending, isError, error };
}

/** Delete everything under the previewed URLs, then refresh every corpus view. */
export function useDeleteCorpusDocuments() {
  const queryClient = useQueryClient();

  return useMutation<DeletionResult, ApiError, string[]>({
    mutationFn: async (sourceUrls) => {
      const { data } = await deleteCorpusDocumentsApiAdminVectorstoreDeletionsPost({
        body: { source_urls: sourceUrls },
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      // The open preview closes with the delete: refetching it would only repeat the selection
      void queryClient.invalidateQueries({
        queryKey: CorpusQueryKey.all,
        predicate: (query) => query.queryKey[1] !== "preview",
      });
    },
  });
}
